import type {
  ApiAdminGuestResponse,
  ApiAdminLoginResponse,
  ApiAdminTenantDetailResponse,
  ApiAdminTenantResponse,
  ApiAdminUserResponse,
  ApiAuthResponse,
  ApiBarChartResponse,
  ApiChartDataPointResponse,
  ApiConversationDetailResponse,
  ApiConversationSummaryResponse,
  ApiAdminBarChart,
  ApiAdminDonutChart,
  ApiAdminLineChart,
  ApiAdminDashboardStatItem,
  ApiDashboardFullResponse,
  ApiDashboardStatItemResponse,
  ApiDashboardStatsResponse,
  ApiDonutChartResponse,
  ApiKnowledgeDetailResponse,
  ApiKnowledgeDocumentResponse,
  ApiLineChartResponse,
  ApiPageResponse,
  ApiPaginatedResponse,
  ApiUserResponse,
} from './model.response';
import {
  DEFAULT_CHART_COLOR_TOKEN,
  isChartColorToken,
  type ChartColorToken,
} from '@/config/color.tokens';
import type {
  AdminTenantDetail,
  AdminTenantSummary,
  AuthSession,
  BarChartData,
  ChartDataPoint,
  ChatMessage,
  ConversationDetail,
  ConversationSummary,
  DashboardStat,
  DashboardStats,
  DonutChartData,
  DonutSegment,
  KnowledgeDocument,
  KnowledgeDocumentDetail,
  LineChartData,
  PaginatedResult,
  Persona,
  TrendDirection,
  User,
  UserRole,
  Guest,
} from './model.type';

function mapRole(role: string): UserRole {
  const normalized = role.toLowerCase();
  if (normalized === 'admin' || normalized === 'moderator' || normalized === 'user') {
    return normalized;
  }
  return 'user';
}

export function toUser(apiUser: ApiUserResponse): User {
  return {
    id: apiUser.id,
    email: apiUser.email,
    name: apiUser.full_name?.trim() || null,
    fullName: apiUser.full_name,
    role: mapRole(apiUser.role),
    avatarUrl: apiUser.avatar_url,
    createdAt: new Date(apiUser.created_at),
    isActive: apiUser.is_active,
  };
}

export function toAdminUser(apiUser: ApiAdminUserResponse): User {
  const localPart = apiUser.email.split('@')[0] ?? apiUser.email;
  const name = apiUser.name?.trim() || null;
  return {
    id: apiUser.id,
    email: apiUser.email,
    name,
    fullName: name || localPart,
    role: 'user',
    avatarUrl: apiUser.pictureUrl?.trim() || null,
    createdAt: new Date(apiUser.createdAt),
    updatedAt: new Date(apiUser.updatedAt),
    isActive: apiUser.enabled,
    tokenBalance: apiUser.tokenBalance,
    tenantId: apiUser.tenantId,
    tenantLabel: apiUser.tenantLabel,
  };
}

export function toAdminTenantSummary(api: ApiAdminTenantResponse): AdminTenantSummary {
  return {
    id: api.id,
    label: api.label,
    enabled: api.enabled ?? true,
  };
}

export function toAdminTenantDetail(api: ApiAdminTenantDetailResponse): AdminTenantDetail {
  return {
    id: api.id,
    label: api.label,
    enabled: api.enabled,
    allowPublicApi: api.allowPublicApi,
    authMode: api.authMode,
    googleClientId: api.googleClientId,
    openrouterKey: api.openrouterKey,
    groqKey: api.groqKey,
    geminiKey: api.geminiKey,
    guestDailyTokens: api.guestDailyTokens,
    signupTokenGrant: api.signupTokenGrant,
    dailyTokenRefill: api.dailyTokenRefill,
    tokensPerMessage: api.tokensPerMessage,
    maxConversationsPerUser: api.maxConversationsPerUser,
    personaName: api.personaName,
    personaSystemPrompt: api.personaSystemPrompt,
  };
}

export function toAdminGuest(apiGuest: ApiAdminGuestResponse): Guest {
  return {
    id: apiGuest.guestId,
    clientInstallId: apiGuest.clientInstallId,
    tokenBalance: apiGuest.tokenBalance,
    tenantId: apiGuest.tenantId,
    tenantLabel: apiGuest.tenantLabel,
  };
}

export function toAdminSession(response: ApiAdminLoginResponse): AuthSession {
  return {
    token: response.token,
    user: {
      id: response.adminId,
      email: response.username,
      name: response.username,
      fullName: response.username,
      role: 'admin',
      avatarUrl: null,
      createdAt: new Date(),
      isActive: true,
    },
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
  };
}

export function toPaginatedFromApiPage<TApi, TModel>(
  response: ApiPageResponse<TApi>,
  mapItem: (item: TApi) => TModel,
  page: number,
  pageSize: number,
): PaginatedResult<TModel> {
  return {
    data: response.items.map(mapItem),
    total: response.totalItems,
    page,
    pageSize,
    totalPages: Math.max(1, response.totalPages),
  };
}

export function toAuthSession(response: ApiAuthResponse): AuthSession {
  return {
    token: response.token,
    user: toUser(response.user),
    expiresAt: new Date(response.expires_at),
  };
}

export function toPaginatedUsers(
  response: ApiPaginatedResponse<ApiUserResponse>,
): PaginatedResult<User> {
  return {
    data: response.data.map(toUser),
    total: response.total,
    page: response.page,
    pageSize: response.page_size,
    totalPages: response.total_pages,
  };
}

function mapTrend(trend: string): TrendDirection {
  if (trend === 'up' || trend === 'down' || trend === 'neutral') {
    return trend;
  }
  return 'neutral';
}

function toChartDataPoint(point: ApiChartDataPointResponse): ChartDataPoint {
  return {
    label: point.label,
    value: point.value,
    ...(point.color_token ? { colorToken: mapColorToken(point.color_token) } : {}),
  };
}

export function toDashboardStats(response: ApiDashboardStatsResponse): DashboardStats {
  return {
    stats: response.stats.map(toDashboardStatItem),
    updatedAt: new Date(response.updated_at),
  };
}

function toDashboardStatItem(item: ApiDashboardStatItemResponse): DashboardStat {
  return {
    id: item.id,
    labelKey: item.label_key,
    value: item.value,
    changePercent: item.change_percent,
    trend: mapTrend(item.trend),
  };
}

function toAdminDashboardStatItem(item: ApiAdminDashboardStatItem): DashboardStat {
  return {
    id: item.id,
    labelKey: item.labelKey,
    value: Number(item.value),
    changePercent: item.changePercent,
    trend: mapTrend(item.trend),
  };
}

function toLineChartFromAdmin(chart: ApiAdminLineChart): LineChartData {
  return {
    id: chart.id,
    titleKey: chart.titleKey,
    points: chart.data.map((point) => ({
      label: point.label,
      value: Number(point.value),
      ...(point.colorToken ? { colorToken: mapColorToken(point.colorToken) } : {}),
    })),
    unit: chart.unit,
  };
}

function toBarChartFromAdmin(chart: ApiAdminBarChart): BarChartData {
  return {
    id: chart.id,
    titleKey: chart.titleKey,
    points: chart.data.map((point) => ({
      label: point.label,
      value: Number(point.value),
      ...(point.colorToken ? { colorToken: mapColorToken(point.colorToken) } : {}),
    })),
  };
}

function toDonutChartFromAdmin(chart: ApiAdminDonutChart): DonutChartData {
  return {
    id: chart.id,
    titleKey: chart.titleKey,
    total: Number(chart.total),
    segments: chart.segments.map((segment) => ({
      labelKey: segment.labelKey,
      value: Number(segment.value),
      colorToken: mapColorToken(segment.colorToken),
    })),
  };
}

export function toDashboardFromFull(response: ApiDashboardFullResponse): {
  stats: DashboardStats;
  messagesTrend: LineChartData;
  activityChart: BarChartData;
  breakdownChart: DonutChartData;
} {
  return {
    stats: {
      stats: response.stats.map(toAdminDashboardStatItem),
      updatedAt: new Date(response.updatedAt),
    },
    messagesTrend: toLineChartFromAdmin(response.messagesTrend),
    activityChart: toBarChartFromAdmin(response.activityChart),
    breakdownChart: toDonutChartFromAdmin(response.breakdownChart),
  };
}

export function toLineChartData(response: ApiLineChartResponse): LineChartData {
  return {
    id: response.id,
    titleKey: response.title_key,
    points: response.data.map(toChartDataPoint),
    unit: response.unit,
  };
}

export function toBarChartData(response: ApiBarChartResponse): BarChartData {
  return {
    id: response.id,
    titleKey: response.title_key,
    points: response.data.map(toChartDataPoint),
  };
}

function mapColorToken(token: string): ChartColorToken {
  return isChartColorToken(token) ? token : DEFAULT_CHART_COLOR_TOKEN;
}

export function toDonutChartData(response: ApiDonutChartResponse): DonutChartData {
  return {
    id: response.id,
    titleKey: response.title_key,
    segments: response.segments.map(
      (segment): DonutSegment => ({
        labelKey: segment.label_key,
        value: segment.value,
        colorToken: mapColorToken(segment.color_token),
      }),
    ),
    total: response.total,
  };
}

function resolveConversationTitle(title: string | null | undefined, updatedAt: Date): string {
  const trimmed = title?.trim();
  if (trimmed) return trimmed;
  return updatedAt.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function toConversationSummary(item: ApiConversationSummaryResponse): ConversationSummary {
  const updatedAt = new Date(item.updatedAt);
  return {
    id: item.conversationId,
    title: resolveConversationTitle(item.title, updatedAt),
    updatedAt,
  };
}

export function toConversationDetail(response: ApiConversationDetailResponse): ConversationDetail {
  return {
    id: response.conversationId,
    messages: response.messages.map(
      (message): ChatMessage => ({
        id: message.id,
        role: message.role,
        content: message.content,
        createdAt: new Date(message.createdAt),
        action: message.action ?? null,
      }),
    ),
    page: response.page + 1,
    pageSize: response.limit,
    totalItems: response.totalItems,
    totalPages: response.totalPages,
    hasNext: response.hasNext,
  };
}

export function toKnowledgeDocument(item: ApiKnowledgeDocumentResponse): KnowledgeDocument {
  return {
    id: item.id,
    title: item.title,
    sourceUrl: item.sourceUrl,
    updatedAt: new Date(item.updatedAt),
    chunkCount: item.chunkCount,
    audience: item.audience ?? 'ALL',
  };
}

export function toKnowledgeDetail(response: ApiKnowledgeDetailResponse): KnowledgeDocumentDetail {
  const firstChunk = response.chunks[0];
  return {
    id: response.id,
    title: response.title,
    sourceUrl: response.sourceUrl,
    updatedAt: new Date(response.updatedAt),
    chunkCount: response.chunks.length,
    content: response.chunks.map((chunk) => chunk.content).join('\n\n'),
    linkUrl: firstChunk?.linkUrl ?? null,
    aliases: firstChunk?.aliases ?? null,
    audience: response.audience ?? 'ALL',
  };
}

export function toPersona(response: {
  id: string;
  name: string;
  systemPrompt: string;
  updatedAt: string;
}): Persona {
  return {
    id: response.id,
    name: response.name,
    systemPrompt: response.systemPrompt,
    updatedAt: new Date(response.updatedAt),
  };
}
